export const SOCIAL_ACTION_VALUATIONS = Object.freeze({
  like: "like",
  dislike: "dislike",
  skip: "omitted",
  discomfort: "reported",
});

export const PEOPLE_COMPLETION_TRANSITIONS = Object.freeze({
  EVENT: "event",
  COMPLETE: "complete",
  RECONCILE: "reconcile",
});

const RATING_INTRO_SESSION_VERSION = "v1";

export function getRatingIntroSessionKey({
  userId,
  eventId,
  requireSocial = false,
  requireSkill = false,
} = {}) {
  if (!userId || !eventId) return null;
  return [
    "vg-rating-intro",
    RATING_INTRO_SESSION_VERSION,
    userId,
    eventId,
    requireSocial ? "social" : "no-social",
    requireSkill ? "skill" : "no-skill",
  ].join(":");
}

export function readRatingIntroSession(storage, key) {
  if (!storage || !key) return null;
  try {
    const parsed = JSON.parse(storage.getItem(key) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    return {
      introCompleted: parsed.introCompleted === true,
      introStep: Number.isInteger(parsed.introStep)
        ? Math.max(0, parsed.introStep)
        : 0,
    };
  } catch (_error) {
    return null;
  }
}

export function writeRatingIntroSession(storage, key, value) {
  if (!storage || !key) return;
  try {
    storage.setItem(
      key,
      JSON.stringify({
        introCompleted: value?.introCompleted === true,
        introStep: Number.isInteger(value?.introStep)
          ? Math.max(0, value.introStep)
          : 0,
      })
    );
  } catch (_error) {
    // Storage can be unavailable in privacy-restricted WebViews. The in-memory
    // task guard still prevents resets for the current mount.
  }
}

export function clearRatingIntroSession(storage, key) {
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch (_error) {
    // See writeRatingIntroSession.
  }
}

export function getRatingInitialPhase({
  hasParticipants = false,
  requiresEventRating = false,
  introCompleted = false,
  hasServerProgress = false,
  decisionRequired = false,
} = {}) {
  if (decisionRequired) {
    return introCompleted || hasServerProgress ? "people_choice" : "intro";
  }
  if (!hasParticipants && requiresEventRating) return "event";
  if (introCompleted || hasServerProgress) return "people";
  return "intro";
}

export function hasRatingServerProgress({
  pending = {},
  peopleFlow = {},
  participants = [],
} = {}) {
  const progressedThroughAssignedContacts =
    peopleFlow?.stage === "decision" &&
    Number(peopleFlow?.primary_total || 0) > 0;

  return Boolean(
    (pending?.require_social && pending?.social_done) ||
      (pending?.require_skill && pending?.skill_done) ||
      (pending?.require_event_rating && pending?.event_done) ||
      ["optional", "deferred"].includes(peopleFlow?.stage) ||
      progressedThroughAssignedContacts ||
      participants.some(
        (person) => person?.alreadyRatedSocial || person?.alreadyRatedSkill
      )
  );
}

export function getPeopleCompletionTransition({
  submission,
  requiresEventRating = false,
} = {}) {
  if (requiresEventRating) {
    return PEOPLE_COMPLETION_TRANSITIONS.EVENT;
  }

  if (
    submission?.pending_completed === true &&
    submission?.still_blocking === false
  ) {
    return PEOPLE_COMPLETION_TRANSITIONS.COMPLETE;
  }

  return PEOPLE_COMPLETION_TRANSITIONS.RECONCILE;
}

export function buildRatingIntroPages({
  requireSocial = false,
  requireSkill = false,
  isSportEvent = false,
} = {}) {
  const modes = [];
  if (requireSocial) modes.push("social");
  if (requireSkill) modes.push("skill");
  if (!modes.length) modes.push(isSportEvent ? "skill" : "social");

  const sharedIntroMode = modes.includes("social") ? "social" : modes[0];
  return [
    ...(modes.includes("social")
      ? [{ mode: "social", kind: "calibration" }]
      : []),
    { mode: sharedIntroMode, kind: "event" },
    ...modes.map((mode) => ({ mode, kind: "guide" })),
    ...(modes.includes("social")
      ? [{ mode: "social", kind: "self_preview" }]
      : []),
  ];
}
